"use client"

/* eslint-disable no-nested-ternary */
import clsx from "clsx"
import Image from "next/image"
import React, { useCallback, useState, useRef, useEffect } from "react"

import { removeImageCropResize } from "@/helpers/utils"
import { VideoItem } from "@/types/video"
import VideoPlayer from "@/ui/components/VideoPlayer"

import VideoArrow from "../../../public/icons/arrow-video.svg"
import FallbackImage from "../../../public/video-covers/videocarouselFallback.png"

export interface CarouselVideos {
  videos: VideoItem[]
}

interface VideoCarouselProps
  extends CarouselVideos,
    React.ComponentPropsWithoutRef<"div"> {
  sectionTitle?: string
  pencilDescription?: string
  subDescription?: string
  darkmode?: boolean
}

const VideoCarousel: React.FC<VideoCarouselProps> = ({
  sectionTitle,
  pencilDescription,
  subDescription,
  darkmode = true,
  videos,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    },
    [],
  )

  // Helper function to get image source with proper fallback
  const getImageSource = useCallback((video: VideoItem) => {
    if (video.videoCover?.url) {
      return video.videoCover.url
    }
    const wistiaThumbnailUrl = video.wistiaVideo?.items?.[0]?.thumbnail?.url
    if (wistiaThumbnailUrl) {
      return removeImageCropResize(wistiaThumbnailUrl)
    }
    return FallbackImage
  }, [])

  const changeSlide = useCallback(
    (newIndex: number) => {
      if (
        isTransitioning ||
        newIndex === currentSlide ||
        newIndex < 0 ||
        newIndex >= videos.length
      )
        return

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      setIsTransitioning(true)

      // After transition completes, update the slide
      timeoutRef.current = setTimeout(() => {
        setCurrentSlide(newIndex)
        setIsTransitioning(false)
        timeoutRef.current = null
      }, 400) // Match CSS transition duration
    },
    [currentSlide, isTransitioning, videos.length],
  )

  const scrollToPrev = useCallback(() => {
    changeSlide(currentSlide - 1)
  }, [changeSlide, currentSlide])

  const scrollToNext = useCallback(() => {
    changeSlide(currentSlide + 1)
  }, [changeSlide, currentSlide])

  const scrollToSlide = useCallback(
    (index: number) => {
      changeSlide(index)
    },
    [changeSlide],
  )

  if (!videos || videos.length === 0) return null

  const canScrollPrev = currentSlide > 0
  const canScrollNext = currentSlide < videos.length - 1
  const currentVideo = videos[currentSlide]
  const nextVideo = videos[currentSlide + 1]

  return (
    <>
      <div className="p-4">
        {pencilDescription ? (
          <p className="mb-4 text-blue-50 heading3">{pencilDescription}</p>
        ) : (
          ""
        )}
        <h4 className="heading1 md:headline4">{sectionTitle || "Videos"}</h4>
        {subDescription ? (
          <p className="my-4 text-gray-90 body2 md:body1">{subDescription}</p>
        ) : (
          ""
        )}
      </div>

      <div
        className={clsx("insights-video-carousel", {
          transitioning: isTransitioning,
        })}
      >
        <div className="py-2">
          <div className="m-auto max-w-7xl">
            <div className="flex flex-col lg:items-center lg:flex-row">
              <div className="flex flex-col w-96">
                <div className="pl-4 insights-carousel-meta">
                  <h4 className="pt-8 heading3 md:heading2 xl:heading1 md:text-right video-title">
                    {currentVideo?.videoName || ""}
                  </h4>
                </div>
                <div className="insights-carousel-up-next">
                  {nextVideo && (
                    <div className="hidden lg:block up-next-section">
                      <div className="pt-20 pl-4 caption2">UP NEXT</div>
                      <button
                        onClick={() => scrollToSlide(currentSlide + 1)}
                        disabled={!canScrollNext || isTransitioning}
                        className="w-full"
                        type="button"
                        aria-label={`Go to next video: ${nextVideo.videoName}`}
                      >
                        <div className="relative mt-4 overflow-hidden rounded-md group aspect-video">
                          <Image
                            sizes="(min-width: 1504px) 1504px, 100vw"
                            width={1504}
                            height={846}
                            src={getImageSource(nextVideo)}
                            className="group-hover:opacity-80 !absolute"
                            alt={`Next video: ${nextVideo.videoName}`}
                            loading="lazy"
                          />
                        </div>
                        <div className="max-w-xs pl-4 mt-4 heading3">
                          {nextVideo.videoName}
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-grow block mt-10 lg:mt-0 lg:ml-24">
                <div className="relative overflow-hidden rounded-md z-10">
                  <div className="insights-carousel-player video-player">
                    {currentVideo && (
                      <VideoPlayer
                        key={currentVideo._id} // Force re-render on video change
                        videoURL={currentVideo.videoLink}
                        title={currentVideo.videoName}
                        wistiaVideo={currentVideo.wistiaVideo}
                        staticImagePlaceholder={
                          <Image
                            sizes="(min-width: 1504px) 1504px, 100vw"
                            width={1504}
                            height={846}
                            src={getImageSource(currentVideo)}
                            className="!absolute"
                            alt={`Current video: ${currentVideo.videoName}`}
                            priority={currentSlide === 0} // Prioritize first video
                          />
                        }
                        disabled={false}
                      />
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-center mt-8 navigation-controls">
                  <button
                    onClick={scrollToPrev}
                    disabled={!canScrollPrev || isTransitioning}
                    type="button"
                    aria-label="Previous Video"
                  >
                    <VideoArrow
                      className={clsx(
                        "rotate-180 mx-3 md:mx-4 lg:mx-5 w-10 h-10 md:h-12 md:w-12 lg:h-14 lg:w-14",
                        darkmode
                          ? !canScrollPrev
                            ? "stroke-gray-60"
                            : "stroke-gray-2.5 hover:stroke-gray-60"
                          : !canScrollPrev
                            ? "stroke-gray-30"
                            : "stroke-gray-60 hover:stroke-gray-30",
                      )}
                      title="Previous Video"
                    />
                  </button>
                  <button
                    onClick={scrollToNext}
                    disabled={!canScrollNext || isTransitioning}
                    type="button"
                    aria-label="Next Video"
                  >
                    <VideoArrow
                      className={clsx(
                        "mx-3 md:mx-4 lg:mx-5 w-10 h-10  md:h-12 md:w-12 lg:h-14 lg:w-14",
                        darkmode
                          ? !canScrollNext
                            ? "stroke-gray-60"
                            : "stroke-gray-2.5 hover:stroke-gray-60"
                          : !canScrollNext
                            ? "stroke-gray-30"
                            : "stroke-gray-60 hover:stroke-gray-30",
                      )}
                      title="Next Video"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default VideoCarousel
